/**
 * 经营驾驶舱（Alice 页面 businessCockpit 的复刻）：把活跃、余额、消耗与真实收款转成充值活动的决策建议。
 *
 * 背景：运营要回答「现在该不该开充值赠送活动、给谁」。后端按近 7 天余额可用天数把活跃付费主体分成八档，
 *   再结合收入与消费环比给出 broad / targeted / observe / wait 四种建议；本页只给建议，不会自动开活动或发放花瓣。
 * 设计意图：
 *   - 后端只返回 key 与数值，标题、说明、分层含义、动作文案全部在 i18n（BIZ.bc）里按 key 取三语，接口里没有中文。
 *   - 主体是账号，近 7 天活跃指有扣费请求的账号；金额美元，余额与消耗折成花瓣（1 美元 = 1000 花瓣）。
 *   - 充值活动由「充值活动」页管理，驾驶舱暂不读取活动状态：「活动互斥」「活动冷却」两个信号恒为满足，页面标注出来，
 *     不假装读到了活动。
 * 关键约束：Alice 的 50 元、20 元档位建议换成等值美元档位（见 BIZ.bc.next 与 BIZ.bc.gift）；后端缓存 60 秒。
 */
import { useEffect, useMemo } from 'react'
import { bizApi, type Cockpit, type CockpitBucket, type SegmentKey } from '@ap/api-client/admin-biz'
import { UNITS_PER_PETAL, UNITS_PER_USD, fmtInt, fmtPetals } from '@ap/api-client/money'
import { A } from '@ap/i18n/admin'
import { BIZ, BIZ_HELP } from '@ap/i18n/admin-biz'
import { C, errorText } from '@ap/i18n/common'
import { intlTag, useI18n } from '@ap/i18n'
import { Link, Notice, navigate, useLoad, useToast } from '@ap/ui'
import { ChartCanvas, Help, SortTh, bizError, chartOpts, growthText, runwayText, useSorted } from '../../alice/biz-kit.tsx'
import { OpsNote, OpsPage, Stat, Stats } from '../../alice/kit.tsx'
import { useUsd } from '../../shared.tsx'

const BUCKET_COLORS: Record<CockpitBucket['key'], string> = {
  lt3: '#dc2626',
  d3_7: '#f59e0b',
  d8_21: '#2563eb',
  d22_60: '#10b981',
  gt60: '#64748b',
  no_spend: '#cbd5e1',
}
const MOMENTUM_RANK = { high: 3, medium: 2, low: 1 } as const

export function BusinessCockpit() {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const usd = useUsd()
  const toast = useToast()
  const load = useLoad<Cockpit>(() => bizApi.cockpit(), [])
  const d = load.data

  useEffect(() => {
    if (load.error) toast.error(bizError(load.error, locale))
  }, [load.error, toast, locale])

  const int = (n: number) => fmtInt(n, tag)
  const pts = (u: number) => t(BIZ.bc.petalsUnit, { n: fmtPetals(u, tag) })
  const days = (v: number | null | undefined) => runwayText(v, (n) => t(BIZ.bc.daysUnit, { n }))
  const H = (k: string) => <Help k={k} help={BIZ_HELP} locale={locale} closeLabel={t(C.close)} />
  const none = t(BIZ.bc.noBaseline)

  const segLabel = (k: string) => (BIZ.bc.seg[k as SegmentKey] ? t(BIZ.bc.seg[k as SegmentKey].label) : k)

  const bucketRows = useMemo(() => (d?.inventory.buckets ?? []).map((b) => ({ ...b, label: t(BIZ.bc.bucket[b.key]) })), [d, locale]) // eslint-disable-line react-hooks/exhaustive-deps
  const segmentRows = useMemo(
    () =>
      (d?.segments ?? []).map((s) => ({
        ...s,
        label: t(BIZ.bc.seg[s.key].label),
        meaning: t(BIZ.bc.seg[s.key].meaning),
        action: t(BIZ.bc.seg[s.key].action),
      })),
    [d, locale], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const targetRows = useMemo(
    () =>
      (d?.priority_users ?? []).map((u) => ({
        ...u,
        segment_label: t(BIZ.bc.seg[u.segment].label),
        suggested_action: t(BIZ.bc.seg[u.segment].action),
        momentum_rank: MOMENTUM_RANK[u.momentum],
      })),
    [d, locale], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const sBuckets = useSorted(bucketRows, { key: 'label', dir: 'asc' }, locale)
  const sSegments = useSorted(segmentRows, { key: 'priority', dir: 'asc' }, locale)
  const sTargets = useSorted(targetRows, { key: 'momentum_rank', dir: 'desc' }, locale)

  const charts = useMemo(() => {
    if (!d) return null
    const labels = d.daily.map((r) => r.date.slice(5))
    const rev = chartOpts()
    rev.scales = {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { beginAtZero: true, position: 'left', grid: { color: 'rgba(0,0,0,.04)' }, ticks: { font: { size: 10 }, callback: (v: unknown) => `$${v}` } },
      y1: { beginAtZero: true, position: 'right', grid: { drawOnChartArea: false }, ticks: { font: { size: 10 }, callback: (v: unknown) => `${v}K` } },
    }
    return {
      revenue: {
        type: 'bar' as const,
        data: {
          labels,
          datasets: [
            { label: t(BIZ.bc.chartRevenue), data: d.daily.map((r) => r.revenue_units / UNITS_PER_USD), backgroundColor: 'rgba(16,185,129,.62)', borderRadius: 4, yAxisID: 'y' },
            {
              label: t(BIZ.bc.chartSpend),
              data: d.daily.map((r) => r.spent_units / UNITS_PER_PETAL / 1000),
              type: 'line' as const,
              borderColor: '#2563eb',
              backgroundColor: 'transparent',
              pointRadius: 2,
              borderWidth: 2,
              tension: 0.25,
              yAxisID: 'y1',
            },
          ],
        },
        options: rev,
      },
      active: {
        type: 'line' as const,
        data: {
          labels,
          datasets: [
            { label: t(BIZ.bc.chartActive), data: d.daily.map((r) => r.active_accounts), borderColor: '#8b5cf6', backgroundColor: 'rgba(139,92,246,.1)', fill: true, pointRadius: 2, borderWidth: 2, tension: 0.25 },
            { label: t(BIZ.bc.chartPayers), data: d.daily.map((r) => r.payers), borderColor: '#f59e0b', backgroundColor: 'transparent', pointRadius: 2, borderWidth: 2, tension: 0.25 },
          ],
        },
        options: chartOpts(),
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, locale])

  const status = d?.decision.status
  const dec = status ? BIZ.bc.dec[status] : null
  const campaignLine = d ? t(BIZ.bc.campaignUnavailable) : ''
  const bucketTotal = (d?.inventory.buckets ?? []).reduce((a, b) => a + b.users, 0)
  const paidInv = d?.inventory.paid
  const unpaidInv = d?.inventory.unpaid
  const trialReady = d?.segments.find((s) => s.key === 'trial_ready')?.users ?? 0

  const triggerCurrent = (k: string, v: number | null): string => {
    if (k === 'balance_pressure') return v === null ? '—' : `${(v * 100).toFixed(1)}%`
    if (k === 'revenue_pressure' || k === 'spend_momentum') return v === null ? none : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`
    if (k === 'inventory_pressure') return v === null ? t(BIZ.bc.noSpend) : t(BIZ.bc.daysUnit, { n: v.toFixed(1) })
    if (k === 'campaign_exclusive') return t(BIZ.bc.campaignNA)
    return t(BIZ.bc.campaignNA)
  }

  return (
    <OpsPage
      eyebrow="GROWTH CONTROL"
      title={t(A.nav.businessCockpit)}
      desc={t(BIZ.bc.desc)}
      chip={d ? t(BIZ.bc.updated, { t: `${d.generated_at.slice(0, 10)} ${d.generated_at.slice(11, 16)}` }) : load.error ? t(BIZ.ca.failed) : t(BIZ.ca.computing)}
    >
      {d?.payments_error && <Notice tone="danger">{t(BIZ.common.paymentsDown)}</Notice>}
      {!!load.error && !d && <Notice tone="danger">{errorText(load.error, locale)}</Notice>}

      <div className={`bc-decision ${status ?? ''}`}>
        <div>
          <div className="ops-eyebrow">
            {t(BIZ.bc.decisionEyebrow)} {H('bcDecision')}
          </div>
          <h2 className="bc-decision-title">{dec ? t(dec.title) : t(BIZ.bc.decisionLoading)}</h2>
          <p className="bc-decision-copy">
            {dec && d ? t(dec.summary, { u: int(d.decision.urgent_payers), n: int(trialReady) }) : t(BIZ.bc.decisionHint)} {campaignLine}
          </p>
          <div className="bc-decision-actions">
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/campaign')}>
              {t(BIZ.bc.goCampaign)}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/gift-redemption')}>
              {t(BIZ.bc.goGift)}
            </button>
          </div>
        </div>
        <div className={`bc-score ${status ?? ''}`}>
          <div>
            <strong>{d ? int(d.decision.readiness_score) : '—'}</strong>
            <span>{t(BIZ.bc.score)}</span>
          </div>
        </div>
      </div>

      <Stats>
        <Stat
          label={<>{t(BIZ.bc.kActive)} {H('bcActive')}</>}
          value={d ? int(d.summary.active_accounts_7d) : '—'}
          sub={d ? t(BIZ.bc.kActiveSub, { h: int(d.summary.high_momentum_orgs), f: int(d.summary.free_only_orgs) }) : t(BIZ.bc.kActiveSubDefault)}
        />
        <Stat
          label={<>{t(BIZ.bc.kBalance)} {H('bcBalance')}</>}
          value={d ? pts(d.summary.active_balance_units) : '—'}
          sub={d ? t(BIZ.bc.kBalanceSub, { v: pts(d.summary.active_spent_units_7d) }) : t(BIZ.bc.kBalanceSubDefault)}
        />
        <Stat label={<>{t(BIZ.bc.kRunway)} {H('bcRunway')}</>} value={d ? days(d.summary.active_balance_runway_days) : '—'} sub={t(BIZ.bc.kRunwaySub)} />
        <Stat
          label={<>{t(BIZ.bc.kUrgent)} {H('bcUrgent')}</>}
          value={d ? int(d.decision.urgent_payers) : '—'}
          sub={d ? t(BIZ.bc.kUrgentSub, { n: int(d.decision.near_window_payers) }) : t(BIZ.bc.kUrgentSubDefault)}
        />
      </Stats>

      <div className="bc-signal-grid">
        {(d?.triggers ?? []).map((tr) => {
          const meta = BIZ.bc.trigger[tr.key as keyof typeof BIZ.bc.trigger]
          return (
            <div key={tr.key} className={`bc-signal${tr.met ? ' met' : ''}`}>
              <div className="bc-signal-head">
                <span>{meta ? t(meta.name) : tr.key}</span>
                <span className="bc-signal-dot" />
              </div>
              <strong>{triggerCurrent(tr.key, tr.value)}</strong>
              <small>{meta ? t(meta.threshold) : ''}</small>
            </div>
          )
        })}
      </div>

      <div className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(BIZ.bc.nextTitle)}</div>
            <div className="ops-section-copy">{t(BIZ.bc.nextCopy)}</div>
          </div>
        </div>
        <div className="bc-action-grid">
          {!d || d.next_actions.length === 0 ? (
            <div className="empty" style={{ padding: 16 }}>
              {t(BIZ.bc.nextEmpty)}
            </div>
          ) : (
            d.next_actions.map((a, i) => {
              const meta = BIZ.bc.next[a.key as keyof typeof BIZ.bc.next]
              return (
                <div key={a.key} className="bc-action">
                  <span className="bc-action-step">{i + 1}</span>
                  <strong>{meta ? t(meta.title, { n: int(a.n ?? 0) }) : a.key}</strong>
                  <p>{meta ? t(meta.detail) : ''}</p>
                </div>
              )
            })
          )}
        </div>
      </div>

      <div className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(BIZ.bc.dontTitle)}</div>
            <div className="ops-section-copy">{t(BIZ.bc.dontCopy)}</div>
          </div>
        </div>
        <div className="bc-dont-list">
          {(d?.donts ?? []).map((x) => {
            const meta = BIZ.bc.dont[x.key as keyof typeof BIZ.bc.dont]
            return (
              <div key={x.key} className="bc-dont">
                <strong>{meta ? t(meta.title) : x.key}</strong>
                <p>{meta ? t(meta.detail, { n: int(x.n ?? 0) }) : ''}</p>
              </div>
            )
          })}
        </div>
      </div>

      <div className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">
              {t(BIZ.bc.invTitle)} {H('bcInventory')}
            </div>
            <div className="ops-section-copy">{t(BIZ.bc.invCopy)}</div>
          </div>
        </div>
        <div className="bc-bucket-bar">
          {(d?.inventory.buckets ?? []).map((b) => (
            <span
              key={b.key}
              title={`${t(BIZ.bc.bucket[b.key])} ${t(BIZ.bc.people, { n: int(b.users) })}`}
              style={{ width: `${bucketTotal ? (b.users / bucketTotal) * 100 : 0}%`, background: BUCKET_COLORS[b.key] }}
            />
          ))}
        </div>
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <SortTh sortKey="label" state={sBuckets.state} onSort={sBuckets.toggle} help={<> {H('bcRunway')}</>}>
                  {t(BIZ.bc.colRunway)}
                </SortTh>
                <SortTh sortKey="users" state={sBuckets.state} onSort={sBuckets.toggle}>
                  {t(BIZ.bc.colOrgs)}
                </SortTh>
                <SortTh sortKey="paid_users" state={sBuckets.state} onSort={sBuckets.toggle}>
                  {t(BIZ.bc.colPaid)}
                </SortTh>
                <SortTh sortKey="unpaid_users" state={sBuckets.state} onSort={sBuckets.toggle}>
                  {t(BIZ.bc.colUnpaid)}
                </SortTh>
                <SortTh sortKey="balance_units" state={sBuckets.state} onSort={sBuckets.toggle}>
                  {t(BIZ.bc.colStock)}
                </SortTh>
              </tr>
            </thead>
            <tbody>
              {sBuckets.sorted.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty">
                    {t(BIZ.ca.computing)}
                  </td>
                </tr>
              ) : (
                sBuckets.sorted.map((b) => (
                  <tr key={b.key}>
                    <td>{b.label}</td>
                    <td>{int(b.users)}</td>
                    <td>{int(b.paid_users)}</td>
                    <td>{int(b.unpaid_users)}</td>
                    <td>{pts(b.balance_units)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="bc-inventory-grid">
          <div className="bc-inventory">
            <small>{t(BIZ.bc.invPaid)}</small>
            <strong>{paidInv ? `${t(BIZ.bc.people, { n: int(paidInv.users) })} · ${days(paidInv.runway_days)}` : '—'}</strong>
            <div className="stat-sub">{paidInv ? t(BIZ.bc.invSub, { s: pts(paidInv.balance_units), v: pts(paidInv.spent_units_7d) }) : '—'}</div>
          </div>
          <div className="bc-inventory">
            <small>{t(BIZ.bc.invUnpaid)}</small>
            <strong>{unpaidInv ? `${t(BIZ.bc.people, { n: int(unpaidInv.users) })} · ${days(unpaidInv.runway_days)}` : '—'}</strong>
            <div className="stat-sub">{unpaidInv ? t(BIZ.bc.invSub, { s: pts(unpaidInv.balance_units), v: pts(unpaidInv.spent_units_7d) }) : '—'}</div>
          </div>
        </div>
      </div>

      <div className="dashboard-chart-grid">
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>
              {t(BIZ.bc.cRevenue)} {H('bcTrend')}
            </span>
            <small>{t(BIZ.bc.cRevenueSub)}</small>
          </div>
          <div className="chart-h-220">{charts && <ChartCanvas config={charts.revenue} label={t(BIZ.bc.cRevenue)} />}</div>
        </div>
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>
              {t(BIZ.bc.cActive)} {H('bcActiveTrend')}
            </span>
            <small>{t(BIZ.bc.cActiveSub)}</small>
          </div>
          <div className="chart-h-220">{charts && <ChartCanvas config={charts.active} label={t(BIZ.bc.cActive)} />}</div>
        </div>
      </div>

      <div className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(BIZ.bc.segTitle)}</div>
            <div className="ops-section-copy">{t(BIZ.bc.segCopy)}</div>
          </div>
        </div>
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <SortTh sortKey="priority" state={sSegments.state} onSort={sSegments.toggle} help={<> {H('bcSegment')}</>}>
                  {t(BIZ.bc.colSegment)}
                </SortTh>
                <SortTh sortKey="users" state={sSegments.state} onSort={sSegments.toggle}>
                  {t(BIZ.bc.colOrgs)}
                </SortTh>
                <SortTh sortKey="balance_units" state={sSegments.state} onSort={sSegments.toggle} help={<> {H('bcBalance')}</>}>
                  {t(BIZ.bc.colStock)}
                </SortTh>
                <SortTh sortKey="spent_units_7d" state={sSegments.state} onSort={sSegments.toggle} help={<> {H('bcSpend')}</>}>
                  {t(BIZ.bc.colSpend7d)}
                </SortTh>
                <SortTh sortKey="median_runway_days" state={sSegments.state} onSort={sSegments.toggle} help={<> {H('bcRunway')}</>}>
                  {t(BIZ.bc.colMedianRunway)}
                </SortTh>
                <SortTh sortKey="meaning" state={sSegments.state} onSort={sSegments.toggle}>
                  {t(BIZ.bc.colMeaning)}
                </SortTh>
                <SortTh sortKey="action" state={sSegments.state} onSort={sSegments.toggle}>
                  {t(BIZ.bc.colAction)}
                </SortTh>
              </tr>
            </thead>
            <tbody>
              {sSegments.sorted.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty">
                    {t(BIZ.ca.computing)}
                  </td>
                </tr>
              ) : (
                sSegments.sorted.map((s) => (
                  <tr key={s.key}>
                    <td>
                      <strong>{s.label}</strong>
                    </td>
                    <td>{int(s.users)}</td>
                    <td>{pts(s.balance_units)}</td>
                    <td>{pts(s.spent_units_7d)}</td>
                    <td>{days(s.median_runway_days)}</td>
                    <td>{s.meaning}</td>
                    <td style={{ fontWeight: 600 }}>{s.action}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(BIZ.bc.giftTitle)}</div>
            <div className="ops-section-copy">{t(BIZ.bc.giftCopy)}</div>
          </div>
        </div>
        <div className="bc-guidance-grid">
          {(d?.gift_guidance ?? []).map((g) => {
            const meta = BIZ.bc.gift[g.key as keyof typeof BIZ.bc.gift]
            return (
              <div key={g.key} className="bc-guidance">
                <div className="bc-guidance-head">
                  <strong>{segLabel(g.key)}</strong>
                  <span className="bc-guidance-count">{t(BIZ.bc.people, { n: int(g.users) })}</span>
                </div>
                <b>{meta ? `${t(meta.strategy)}${t(BIZ.bc.colon)}${t(meta.offer)}` : g.key}</b>
                <p>{meta ? t(meta.reason) : ''}</p>
              </div>
            )
          })}
        </div>
      </div>

      <div className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(BIZ.bc.targetTitle)}</div>
            <div className="ops-section-copy">{t(BIZ.bc.targetCopy)}</div>
          </div>
          <small>{d ? t(BIZ.bc.targetCount, { n: int(d.priority_users.length) }) : ''}</small>
        </div>
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <SortTh sortKey="name" state={sTargets.state} onSort={sTargets.toggle}>
                  {t(BIZ.bc.colOrg)}
                </SortTh>
                <SortTh sortKey="segment_label" state={sTargets.state} onSort={sTargets.toggle}>
                  {t(BIZ.bc.colSegment)}
                </SortTh>
                <SortTh sortKey="momentum_rank" state={sTargets.state} onSort={sTargets.toggle} help={<> {H('bcMomentum')}</>}>
                  {t(BIZ.bc.colMomentum)}
                </SortTh>
                <SortTh sortKey="active_days_7d" state={sTargets.state} onSort={sTargets.toggle}>
                  {t(BIZ.bc.colActiveDays)}
                </SortTh>
                <SortTh sortKey="balance_units" state={sTargets.state} onSort={sTargets.toggle}>
                  {t(BIZ.bc.colBalance)}
                </SortTh>
                <SortTh sortKey="spent_units_7d" state={sTargets.state} onSort={sTargets.toggle}>
                  {t(BIZ.bc.colSpend7d)}
                </SortTh>
                <SortTh sortKey="runway_days" state={sTargets.state} onSort={sTargets.toggle}>
                  {t(BIZ.bc.colRunwayEst)}
                </SortTh>
                <SortTh sortKey="lifetime_paid_units" state={sTargets.state} onSort={sTargets.toggle}>
                  {t(BIZ.bc.colPaidHistory)}
                </SortTh>
                <SortTh sortKey="suggested_action" state={sTargets.state} onSort={sTargets.toggle}>
                  {t(BIZ.bc.colAction)}
                </SortTh>
              </tr>
            </thead>
            <tbody>
              {sTargets.sorted.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty">
                    {d ? t(BIZ.bc.targetEmpty) : t(BIZ.ca.computing)}
                  </td>
                </tr>
              ) : (
                sTargets.sorted.map((u) => (
                  <tr key={u.account_id}>
                    <td>
                      <Link href={`/accounts/${u.account_id}`}>{u.name}</Link>
                      <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}> ID {u.account_id.slice(0, 8)}</span>
                    </td>
                    <td>{u.segment_label}</td>
                    <td>{t(BIZ.bc.momentum[u.momentum])}</td>
                    <td>{int(u.active_days_7d)}</td>
                    <td>{pts(u.balance_units)}</td>
                    <td>
                      {pts(u.spent_units_7d)}
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t(BIZ.bc.prev, { v: pts(u.spent_units_prev_7d) })}</div>
                    </td>
                    <td>{days(u.runway_days)}</td>
                    <td>{t(BIZ.bc.paidHistory, { n: int(u.paid_orders), v: usd(u.lifetime_paid_units) })}</td>
                    <td style={{ fontWeight: 600 }}>{u.suggested_action}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <OpsNote>
          {d
            ? t(BIZ.bc.methodWindow, {
                r: usd(d.window.revenue_7d_units),
                rg: growthText(d.window.revenue_growth, none),
                s: pts(d.window.spend_7d_units),
                sg: growthText(d.window.spend_growth, none),
              })
            : ''}{' '}
          {t(BIZ.bc.methodNote)}
        </OpsNote>
      </div>
    </OpsPage>
  )
}
